import {
  afterRenderEffect,
  Component,
  effect,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  Signal,
  SimpleChanges,
  untracked,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { map, take } from 'rxjs/operators';
import {
  BillingTermDialogComponent,
  BillingTermDialogInput,
} from '../billing-term-dialog/billing-term-dialog.component';
import { galaxyFrequencyToRevenuePeriod } from '../billing-term-dialog/sales-to-galaxy-frequency.pipe';
import { InventoryItemWithPricing } from '../common/inventory-item';
import { InventoryListItem } from '../common/item-list/type';
import { ItemPricingTableBaseComponent, ItemRow, ItemRowChangeEvent } from '../common/item-pricing-table-base';
import { ItemSelectorDialogComponent } from '../item-selector/item-selector-dialog/item-selector-dialog.component';
import { ItemPricingTableConfig } from './interface';
import { BillingTermCallbackFn, ItemPricingTableService, ItemsChangedEvent } from './item-pricing-table.service';

export const ITEM_PRICING_TABLE_SELECTOR_PROVIDERS = [ItemPricingTableService];

@Component({
  selector: 'inventory-ui-item-pricing-table',
  templateUrl: './item-pricing-table.component.html',
  styleUrl: './item-pricing-table.component.scss',
  providers: [ITEM_PRICING_TABLE_SELECTOR_PROVIDERS],
  standalone: false,
})
export class ItemPricingTableComponent implements OnChanges {
  private _config?: ItemPricingTableConfig;
  get config(): ItemPricingTableConfig | undefined {
    return this._config;
  }

  @Input()
  set config(config: Omit<ItemPricingTableConfig, 'selectionEditable'>) {
    this._config = {
      ...config,
      selectionEditable: config.canAddItems,
    };
  }

  @Input()
  set selectedItems(items: InventoryItemWithPricing[]) {
    this.service.initItems(items);
  }

  @Input() viewOnly = false;
  @Input() currencyOverride = '';
  @Input() showRetailFrequencySuffix = false;
  @Output() itemsChanged = new EventEmitter<ItemsChangedEvent>();
  @Output() validityChanged = new EventEmitter<boolean>();
  @Output() wholesaleTotalChanged = new EventEmitter<number>();

  @ViewChild('itemPricingTable') itemPricingTable!: ItemPricingTableBaseComponent;

  items: Signal<InventoryItemWithPricing[]>;
  activeItems: Signal<InventoryListItem[]>;

  constructor(
    private service: ItemPricingTableService,
    private dialog: MatDialog,
    private translateService: TranslateService,
    private snackbarService: SnackbarService,
  ) {
    this.items = this.service.items;
    this.activeItems = this.service.activeItems;

    afterRenderEffect({
      write: () => {
        if (this.itemPricingTable) {
          const ptItems = this.itemPricingTable.getItems();
          untracked(() => this.service.updateItemsWithPricing(ptItems));
        }
      },
    });

    effect(() => {
      const latestEvent = this.service.latestEvent();
      if (latestEvent) {
        this.notifyUserOfChanges(latestEvent);
        this.itemsChanged.emit(latestEvent);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    const currencyChanged = changes?.currencyOverride?.currentValue !== changes?.currencyOverride?.previousValue;
    if (changes?.config?.currentValue !== changes?.config?.previousValue || currencyChanged) {
      const partnerId = this.config?.partnerId ?? '';
      const marketId = this.config?.marketId ?? '';
      const businessId = this.config?.businessId ?? '';
      const enforceDependencies = this.config?.enforceDependencies ?? false;
      this.service.setConfig({
        partnerId,
        marketId,
        businessId,
        currencyOverride: this.currencyOverride,
        enforceDependencies: enforceDependencies,
      });

      if (currencyChanged) {
        this.service.updateItemsCurrency(this.currencyOverride);
      }
    }
  }

  onAddItems(): void {
    this.dialog
      .open(ItemSelectorDialogComponent, {
        data: {
          config: this.config,
          selectedItems: this.items(),
          immutableSelectedItems: this.activeItems(),
          availableItems: this.items().concat(this.activeItems()),
        },
        width: '1000px',
        maxWidth: '95vw',
        height: '900px',
        minHeight: '420px',
        maxHeight: '85vh',
      })
      .afterClosed()
      .pipe(
        map((output) => output?.selectedItems ?? []),
        take(1),
      )
      .subscribe((newItems) => {
        if (newItems.length > 0) {
          this.service.overwriteItems(newItems);
        }
      });
  }

  onUpdatedItemRow(changeEvent: ItemRowChangeEvent): void {
    const itemRow = changeEvent.item;
    switch (changeEvent.action) {
      case 'add':
        this.service.addItems([itemRow]);
        break;
      case 'delete':
        this.service.removeItemMatchingRow(itemRow);
        break;
      case 'add-billing-term':
        this.service.addNewBillingTermForItemMatchingRow(itemRow, this.openBillingTermDialog(itemRow));
        break;
      case 'edit-billing-term':
        this.service.editBillingTermForItemMatchingRow(itemRow, this.openBillingTermDialog(itemRow));
        break;
    }
  }

  onValid(valid: boolean): void {
    this.validityChanged.emit(valid);
  }

  onWholesaleTotal(total: number): void {
    this.wholesaleTotalChanged.emit(total);
  }

  private openBillingTermDialog(itemRow: ItemRow): BillingTermCallbackFn {
    return (editing?: boolean) => {
      let billingTerm = itemRow.billingTerm;
      if (!editing) {
        billingTerm = undefined;
      }
      return this.dialog
        .open(BillingTermDialogComponent, {
          data: {
            itemId: itemRow.itemId,
            name: itemRow.name,
            editionName: itemRow.type === 'app' ? (itemRow.edition?.name ?? undefined) : undefined,
            iconUrl: itemRow.iconUrl,
            revenueComponent: {
              revenue: itemRow.retailPricing.total,
              period: galaxyFrequencyToRevenuePeriod(itemRow.retailPricing.frequency),
            },
            startDate: billingTerm?.startDate,
            endDate: billingTerm?.endDate,
            contractStartDate: this.config?.billingTermsConfig?.defaultStartDate,
            canAddAnotherTerm: this.config?.canAddItems && itemRow.isMultiactivatable,
            nonBillable: itemRow.pricing?.nonBillable,
            canEditInvoices: this.config?.canEditInvoices,
            canScheduleActivations: this.config?.canScheduleActivations,
            duration: billingTerm?.duration,
          } as BillingTermDialogInput,
          maxWidth: '90vw',
          maxHeight: '90vh',
          width: '500px',
        })
        .afterClosed()
        .pipe(take(1));
    };
  }

  private notifyUserOfChanges(changeEvent: ItemsChangedEvent): void {
    // ItemSelectorComponent handles adding dependencies and notifying the user
    if (changeEvent.type === 'delete' && changeEvent.source === 'dependency') {
      const translation = 'INVENTORY_UI.ITEM_SELECTOR.DEPENDENCIES.REMOVAL_NOTIFICATION';
      const notification = this.translateService.instant(translation, {
        itemNames: changeEvent.items.map((item) => item.name).join(', '),
      });
      this.snackbarService.openSuccessSnack(notification);
    }
  }
}
